// revoke — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'revoke' })` op (DSE2 §3.F2).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice F2 — GRANT / REVOKE UI.
//
// Per-engine support (matches DSE2 dialect.grant):
//   - mssql / postgres / mysql / oracle: supported.
//   - sqlite: rejected — sqlite has no auth model.
//   - mysql / oracle: cascade not supported.
//   - postgres: pgsqlGrantOptionFor + pgsqlAllPrivileges shortcuts are
//     postgres-only.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, RevokeDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface RevokeArgs {
  readonly revoke: RevokeDef;
}

export function buildRevokeEdit(args: RevokeArgs): DdlEdit {
  return { op: 'revoke', revoke: args.revoke };
}

export async function commitRevoke(
  ctx: VqeDdlEditContext,
  args: RevokeArgs,
): Promise<void> {
  const edit = buildRevokeEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isRevokeSupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getRevokeUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return 'SQLite has no auth model — connection-level access is OS / file-permission driven.';
  }
  return '';
}

export function isRevokeCascadeSupported(engine: SqlEngine): boolean {
  return engine === 'mssql' || engine === 'postgres';
}

export function validateRevokeForEngine(
  engine: SqlEngine,
  args: RevokeArgs,
): string | null {
  if (!isRevokeSupported(engine)) {
    return getRevokeUnsupportedTooltip(engine);
  }
  const r = args.revoke;
  const allShortcut = engine === 'postgres' && r.pgsqlAllPrivileges === true;
  if (!allShortcut && r.privileges.length === 0) {
    return 'At least one privilege is required (e.g. SELECT, INSERT, EXECUTE).';
  }
  if (r.object.kind !== 'system' && r.object.kind !== 'role') {
    if (!r.object.name || r.object.name.trim().length === 0) {
      return `REVOKE ON ${r.object.kind.toUpperCase()} requires an object name.`;
    }
  }
  if (r.grantees.length === 0) {
    return 'At least one revokee is required.';
  }
  for (const p of r.grantees) {
    if (p.kind !== 'public' && (!p.name || p.name.trim().length === 0)) {
      return `Revokee of kind '${p.kind}' requires a name.`;
    }
  }
  if (r.cascade && !isRevokeCascadeSupported(engine)) {
    return `${engine} REVOKE does not support CASCADE.`;
  }
  if (engine !== 'postgres'
    && (r.pgsqlAllPrivileges || r.pgsqlGrantOptionFor)) {
    return `${engine} does not accept postgres-only fields (pgsqlAllPrivileges / pgsqlGrantOptionFor).`;
  }
  if (engine !== 'mssql' && r.mssqlGrantedBy !== undefined) {
    return `${engine} does not accept mssqlGrantedBy.`;
  }
  if (engine === 'oracle'
    && (r.object.kind === 'schema' || r.object.kind === 'database'
      || r.object.kind === 'tablespace')) {
    return `Oracle: schema / database / tablespace privileges are expressed via system privileges. Use 'system' object kind.`;
  }
  if (engine !== 'oracle' && r.object.kind === 'system') {
    return `${engine} does not support 'system' object kind — system privileges are oracle-specific.`;
  }
  return null;
}
